import { useCallback, useEffect, useRef } from 'react';

import createSharedState from 'hooks/createSharedState';

import useVoiceState from './useVoiceState';

const [useSharedUtterance, _getSharedStateBump, setSharedUtterance] =
  createSharedState<SpeechSynthesisUtterance | null>(null);

if ('speechSynthesis' in window) {
  window.speechSynthesis.onvoiceschanged = () => {
    const voices = window.speechSynthesis.getVoices();
    // 'Google US English' 200 chars per request limit
    // Nicky
    // Moira
    const voice: SpeechSynthesisVoice | null =
      voices.find((aVoice) => aVoice.voiceURI === 'Nicky') || voices.find((aVoice) => aVoice.default) || null;
    const utterance = new SpeechSynthesisUtterance();
    utterance.lang = 'en-US';
    utterance.pitch = 1;
    utterance.rate = 1.2;
    utterance.voice = voice;
    setSharedUtterance(utterance);
  };
}

type Subscriber = {
  onEnd: () => void;
};
let subscribers: Subscriber[] = [];

const useSpeechSynthesis = (onEnd?: () => void) => {
  const [utterance] = useSharedUtterance();
  const [, setVoiceState] = useVoiceState();

  const subscriberRef = useRef<Subscriber>();
  useEffect(() => {
    if (onEnd) {
      subscriberRef.current = { onEnd };
      subscribers.push(subscriberRef.current);
    }
    return () => {
      subscribers = subscribers.filter((subscriber) => subscriber !== subscriberRef.current);
    };
  }, [onEnd]);
  useEffect(() => {
    if (utterance) {
      utterance.onstart = () => {
        setVoiceState('SPEAKING');
      };
      utterance.onend = () => {
        setVoiceState('IDLE');
        subscribers.forEach((subscriber) => subscriber?.onEnd?.());
      };
      utterance.onerror = () => {
        setVoiceState('IDLE');
        subscribers.forEach((subscriber) => subscriber?.onEnd?.());
      };
      setVoiceState('IDLE');
    } else {
      setVoiceState('INIT');
    }
  }, [utterance, setVoiceState]);

  const speak = useCallback(
    (message: string | null) => {
      if ('speechSynthesis' in window && utterance) {
        window.speechSynthesis?.cancel?.();
        if (message) {
          utterance.text = message;
          window.speechSynthesis?.speak?.(utterance);
        }
      }
    },
    [utterance],
  );

  return speak;
};

export default useSpeechSynthesis;
